/**
 * useChannelRoi.ts — React hooks for Channel ROI module
 * Issue #137
 *
 * All API calls use @jinzhuan/api-client (no hand-written fetch/axios).
 * All Decimal-sensitive fields are passed as strings.
 */

import { useState, useCallback } from 'react';
import { createApiClient, ApiError } from '@/lib/adminApiClient';
import type { ApiComponents, ApiOperations } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// ─── Query param type helpers ─────────────────────────────────────────────────
type ActivityListQuery = NonNullable<ApiOperations['ChannelRoiController_listActivities']['parameters']['query']>;
type CostListQuery = NonNullable<ApiOperations['ChannelRoiController_listCosts']['parameters']['query']>;
type EvaluationListQuery = NonNullable<ApiOperations['ChannelRoiController_listEvaluations']['parameters']['query']>;
type CommissionListQuery = NonNullable<ApiOperations['ChannelRoiController_listCommissionItems']['parameters']['query']>;
type CostSumQuery = ApiOperations['ChannelRoiController_sumCostsByPartner']['parameters']['query'];

// ─── DTO type aliases ─────────────────────────────────────────────────────────

export type ChannelContactDto = ApiComponents['schemas']['ChannelContactDto'];
export type CreateChannelContactDto = ApiComponents['schemas']['CreateChannelContactDto'];

export type LeadSourceDto = ApiComponents['schemas']['LeadSourceDto'];
export type CreateLeadSourceDto = ApiComponents['schemas']['CreateLeadSourceDto'];

export type BusinessDevActivityDto = ApiComponents['schemas']['BusinessDevActivityDto'];
export type CreateBusinessDevActivityDto = ApiComponents['schemas']['CreateBusinessDevActivityDto'];
export type UpdateBusinessDevActivityDto = ApiComponents['schemas']['UpdateBusinessDevActivityDto'];

export type ChannelCostDto = ApiComponents['schemas']['ChannelCostDto'];
export type CreateChannelCostDto = ApiComponents['schemas']['CreateChannelCostDto'];

export type ChannelEffectEvaluationDto = ApiComponents['schemas']['ChannelEffectEvaluationDto'];
export type CreateChannelEffectEvaluationDto = ApiComponents['schemas']['CreateChannelEffectEvaluationDto'];
export type UpdateChannelEffectEvaluationDto = ApiComponents['schemas']['UpdateChannelEffectEvaluationDto'];

export type ChannelCommissionItemDto = ApiComponents['schemas']['ChannelCommissionItemDto'];
export type CreateChannelCommissionItemDto = ApiComponents['schemas']['CreateChannelCommissionItemDto'];
export type TransitionCommissionStatusDto = ApiComponents['schemas']['TransitionCommissionStatusDto'];
export type TransitionCooperationStatusDto = ApiComponents['schemas']['TransitionCooperationStatusDto'];

// ─── Cooperation Status Labels ────────────────────────────────────────────────

export type ChannelPartnerCooperationStatus =
  | 'prospecting'
  | 'negotiating'
  | 'contracted'
  | 'active_cooperation'
  | 'suspended_cooperation'
  | 'terminated'
  | 'archived';

export const COOPERATION_STATUS_LABELS: Record<ChannelPartnerCooperationStatus, string> = {
  prospecting: '潜在开发',
  negotiating: '洽谈中',
  contracted: '已签约',
  active_cooperation: '合作中',
  suspended_cooperation: '暂停合作',
  terminated: '已终止',
  archived: '已归档',
};

export type CommissionStatus =
  | 'pending_settlement'
  | 'under_review'
  | 'approved'
  | 'paid'
  | 'cancelled'
  | 'clawback';

export const COMMISSION_STATUS_LABELS: Record<CommissionStatus, string> = {
  pending_settlement: '待结算',
  under_review: '审核中',
  approved: '已审批',
  paid: '已支付',
  cancelled: '已取消',
  clawback: '追回',
};

export type ActivityStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled';

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  planned: '计划中',
  in_progress: '进行中',
  completed: '已完成',
  cancelled: '已取消',
};

export type EvaluationStatus = 'draft' | 'confirmed' | 'archived';

export const EVALUATION_STATUS_LABELS: Record<EvaluationStatus, string> = {
  draft: '草稿',
  confirmed: '已确认',
  archived: '已归档',
};

// ─── Error types ──────────────────────────────────────────────────────────────

export interface ChannelRoiApiError {
  message: string;
  status?: number;
}

function mapStatusMessage(status: number | undefined): string | null {
  if (status === 401) return '会话已失效，请重新登录后再试';
  if (status === 403) return '当前角色没有渠道 ROI 模块访问权限';
  return null;
}

function responseErrToApiError(err: unknown, fallback: string): ChannelRoiApiError {
  if (err && typeof err === 'object' && 'message' in err) {
    return { message: String((err as { message: unknown }).message) };
  }
  return { message: fallback };
}

function thrownToApiError(err: unknown): ChannelRoiApiError {
  if (err instanceof ApiError) {
    const friendly = mapStatusMessage(err.status);
    return { message: friendly ?? err.message, status: err.status };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

/**
 * Channel ROI list endpoints currently return `{ data, total }` envelopes at
 * runtime even though the OpenAPI contract declares them as plain arrays —
 * the same drift Issue #238 fixed for `/crm/customers`. Until the contract
 * catches up, every list hook funnels its raw response through this guard so
 * the detail page (Issue #323) can no longer end up `.map`-ing an envelope
 * object and crashing into the global ErrorBoundary.
 *
 * Accepted shapes:
 *   - `readonly T[]`         → returned as-is.
 *   - `{ data: T[], ... }`   → unwrapped to `data`.
 *   - `undefined` / `null`   → treated as empty list (openapi-fetch returns
 *                              `undefined` for 204/empty bodies).
 *   - anything else          → empty list; the caller will not crash, and
 *                              upstream non-2xx responses are already
 *                              surfaced via `setError` before this point.
 */
function unwrapListBody<T>(body: unknown): readonly T[] {
  if (body === undefined || body === null) return [];
  if (Array.isArray(body)) return body as readonly T[];
  if (typeof body === 'object') {
    const inner = (body as { readonly data?: unknown }).data;
    if (Array.isArray(inner)) return inner as readonly T[];
  }
  return [];
}

// Initialize API client
// 渠道 ROI 端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ─── Cooperation Status Transition ────────────────────────────────────────────

export function useTransitionCooperationStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const transition = useCallback(
    async (partnerId: string, body: TransitionCooperationStatusDto): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/channel-roi/partners/{partnerId}/cooperation-status/transition',
          {
            params: { path: { partnerId } },
            body,
          },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '状态转换失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, transition };
}

// ─── ChannelContact Hooks ─────────────────────────────────────────────────────

export function useChannelContactList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (params?: {
      channelPartnerId?: string;
      page?: number;
      limit?: number;
    }): Promise<readonly ChannelContactDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/channel-roi/contacts', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取联系人列表失败'));
          return null;
        }
        return unwrapListBody<ChannelContactDto>(data);
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useChannelContactCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const create = useCallback(
    async (body: CreateChannelContactDto): Promise<Readonly<ChannelContactDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/channel-roi/contacts', {
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建联系人失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, create };
}

export function useChannelContactDelete() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.DELETE('/api/v1/channel-roi/contacts/{id}', {
          params: { path: { id } },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '删除联系人失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, remove };
}

// ─── LeadSource Hooks ─────────────────────────────────────────────────────────

export function useLeadSourceByCustomer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (customerId: string): Promise<Readonly<LeadSourceDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET(
          '/api/v1/channel-roi/lead-sources/customer/{customerId}',
          { params: { path: { customerId } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取线索来源失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useLeadSourceList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (params?: {
      channelPartnerId?: string;
      sourceType?: string;
      page?: number;
      limit?: number;
    }): Promise<readonly LeadSourceDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/channel-roi/lead-sources', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取线索来源列表失败'));
          return null;
        }
        return unwrapListBody<LeadSourceDto>(data);
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useLeadSourceCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const create = useCallback(
    async (body: CreateLeadSourceDto): Promise<Readonly<LeadSourceDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/channel-roi/lead-sources', {
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建线索来源失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, create };
}

// ─── BusinessDevActivity Hooks ────────────────────────────────────────────────

export function useActivityList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (params?: ActivityListQuery): Promise<readonly BusinessDevActivityDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/channel-roi/activities', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取活动列表失败'));
          return null;
        }
        return unwrapListBody<BusinessDevActivityDto>(data);
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useActivityCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const create = useCallback(
    async (body: CreateBusinessDevActivityDto): Promise<Readonly<BusinessDevActivityDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/channel-roi/activities', {
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建活动失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, create };
}

export function useActivityUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const update = useCallback(
    async (
      id: string,
      body: UpdateBusinessDevActivityDto,
    ): Promise<Readonly<BusinessDevActivityDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PATCH(
          '/api/v1/channel-roi/activities/{id}',
          {
            params: { path: { id } },
            body,
          },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '更新活动失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, update };
}

export function useActivityDelete() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.DELETE(
          '/api/v1/channel-roi/activities/{id}',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '删除活动失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, remove };
}

// ─── ChannelCost Hooks ────────────────────────────────────────────────────────

export function useChannelCostList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (params?: CostListQuery): Promise<readonly ChannelCostDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/channel-roi/costs', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取成本列表失败'));
          return null;
        }
        return unwrapListBody<ChannelCostDto>(data);
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useChannelCostCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const create = useCallback(
    async (body: CreateChannelCostDto): Promise<Readonly<ChannelCostDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/channel-roi/costs', {
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建成本记录失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, create };
}

export function useChannelCostSum() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetchSum = useCallback(
    async (params: CostSumQuery): Promise<{ channelPartnerId: string; totalCost: string } | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/channel-roi/costs/sum', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取成本汇总失败'));
          return null;
        }
        if (!data) return null;
        return {
          channelPartnerId: data.channelPartnerId ?? '',
          totalCost: data.totalCost ?? '0',
        };
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetchSum };
}

// ─── ChannelEffectEvaluation Hooks ────────────────────────────────────────────

export function useEvaluationList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (params?: EvaluationListQuery): Promise<readonly ChannelEffectEvaluationDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET('/api/v1/channel-roi/evaluations', {
          params: { query: params },
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取效果评价列表失败'));
          return null;
        }
        return unwrapListBody<ChannelEffectEvaluationDto>(data);
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useEvaluationCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const create = useCallback(
    async (
      body: CreateChannelEffectEvaluationDto,
    ): Promise<Readonly<ChannelEffectEvaluationDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/channel-roi/evaluations', {
          body,
        });
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建效果评价失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, create };
}

export function useEvaluationUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const update = useCallback(
    async (
      id: string,
      body: UpdateChannelEffectEvaluationDto,
    ): Promise<Readonly<ChannelEffectEvaluationDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.PATCH(
          '/api/v1/channel-roi/evaluations/{id}',
          {
            params: { path: { id } },
            body,
          },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '更新效果评价失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, update };
}

// ─── ChannelCommissionItem Hooks ──────────────────────────────────────────────

export function useCommissionItemList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const fetch = useCallback(
    async (params?: CommissionListQuery): Promise<readonly ChannelCommissionItemDto[] | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.GET(
          '/api/v1/channel-roi/commission-items',
          { params: { query: params } },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '获取分成明细列表失败'));
          return null;
        }
        return unwrapListBody<ChannelCommissionItemDto>(data);
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, fetch };
}

export function useCommissionItemCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const create = useCallback(
    async (
      body: CreateChannelCommissionItemDto,
    ): Promise<Readonly<ChannelCommissionItemDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST(
          '/api/v1/channel-roi/commission-items',
          { body },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '创建分成明细失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, create };
}

export function useTransitionCommissionStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChannelRoiApiError | null>(null);
  const transition = useCallback(
    async (
      id: string,
      body: TransitionCommissionStatusDto,
    ): Promise<Readonly<ChannelCommissionItemDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST(
          '/api/v1/channel-roi/commission-items/{id}/transition',
          {
            params: { path: { id } },
            body,
          },
        );
        if (apiErr) {
          setError(responseErrToApiError(apiErr, '状态转换失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, transition };
}
